import type { DocumentSlot } from '@sportello-balneari/shared-components';

export interface SlotMember {
  name: string;
  description?: string | null;
  condition?: string | null;
}

export interface EnrichedSlot {
  slot: DocumentSlot;
  templateReference?: string | null;
  /**
   * Not named `templateUrl`: the Angular vite plugin rewrites every property
   * with that name into an import of the referenced file, so `templateUrl: null`
   * makes the whole module unresolvable under Vitest.
   */
  templateDownloadUrl?: string | null;
  members: SlotMember[];
}

export interface RequirementInput {
  name: string;
  description?: string | null;
  isMandatory: boolean;
  condition?: string | null;
  templateReference?: string | null;
  templateAttachmentId?: string | null;
}

export interface AttachmentInput {
  id: string;
  url: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Converts a flat list of requirements (from one envelope) into upload slots,
 * applying grouping rules:
 *
 * - Requirements sharing the same non-null templateAttachmentId → single slot
 *   (slot is mandatory if any member is; label = templateReference for N>1, name for N=1)
 * - Requirements with null templateAttachmentId → one slot each, never grouped
 *
 * Never group across envelopes — call once per envelope.
 */
export function requirementsToSlots(
  requirements: RequirementInput[],
  attachments: AttachmentInput[],
): EnrichedSlot[] {
  const result: EnrichedSlot[] = [];
  const groupedById = new Map<string, EnrichedSlot>();

  for (const req of requirements) {
    if (req.templateAttachmentId) {
      const existing = groupedById.get(req.templateAttachmentId);

      if (existing) {
        existing.members.push({
          name: req.name,
          description: req.description,
          condition: req.condition,
        });

        if (req.isMandatory) {
          existing.slot = { ...existing.slot, required: true };
        }

        if (!existing.templateReference && req.templateReference) {
          existing.templateReference = req.templateReference;
        }

        // Rule 4: once multi-member, label becomes the template reference name
        if (existing.members.length === 2) {
          existing.slot = {
            ...existing.slot,
            label: existing.templateReference ?? existing.slot.label,
          };
        }
      } else {
        const templateDownloadUrl =
          attachments.find((a) => a.id === req.templateAttachmentId)?.url ??
          null;

        const enriched: EnrichedSlot = {
          slot: {
            id: `template-${req.templateAttachmentId}`,
            label: req.name,
            required: req.isMandatory,
            multiple: true,
          },
          templateReference: req.templateReference,
          templateDownloadUrl,
          members: [
            {
              name: req.name,
              description: req.description,
              condition: req.condition,
            },
          ],
        };

        result.push(enriched);
        groupedById.set(req.templateAttachmentId, enriched);
      }
    } else {
      // Rule 2: null templateAttachmentId → always its own slot
      result.push({
        slot: {
          id: slugify(req.name),
          label: req.name,
          required: req.isMandatory,
          multiple: true,
        },
        templateReference: req.templateReference,
        templateDownloadUrl: null,
        members: [
          {
            name: req.name,
            description: req.description,
            condition: req.condition,
          },
        ],
      });
    }
  }

  return result;
}
